import { z } from 'zod'
import { BRAZILIAN_STATES } from '../constants/brazilianStates'
import { validateCEP } from '../utils/validation'

// Error messages in Portuguese
const errorMessages = {
  required: 'Este campo é obrigatório',
  invalidEmail: 'Email inválido',
  invalidCEP: 'CEP inválido. Use o formato 00000-000',
  invalidPropertyCode: 'Código deve ter 3-10 caracteres alfanuméricos',
  minValue: (min: number) => `Valor deve ser maior que ${min}`,
  maxValue: (max: number) => `Valor deve ser menor que ${max}`,
  minLength: (min: number) => `Deve ter pelo menos ${min} caracteres`,
  maxLength: (max: number) => `Deve ter no máximo ${max} caracteres`,
}

// Schema factory function
export const createPropertySchema = (staysProperty: boolean = false) => {
  // Step 1: Basic Information (Required)
  let basicInfoSchema = z.object({
    title: z
      .string()
      .min(1, errorMessages.required)
      .min(3, errorMessages.minLength(3))
      .max(100, errorMessages.maxLength(100)),

    address: z.object({
      countryCode: z.string().default('BR'),
      stateCode: z.enum(
        BRAZILIAN_STATES.map((s) => s.value) as [string, ...string[]],
        {
          errorMap: () => ({ message: 'Selecione um estado válido' }),
        }
      ),
      city: z.string().min(3, errorMessages.required),
      region: z.string().min(3, errorMessages.required),
      street: z.string().min(3, errorMessages.required),
      streetNumber: z.string().min(1, errorMessages.required),
      zip: z
        .string()
        .min(1, errorMessages.required)
        .refine(validateCEP, errorMessages.invalidCEP),
    }),

    description: z.string().optional(),
  })

  const basicInfoSchemaStaysExtended = z.object({
    _idproperty: z.string().min(1, errorMessages.required),
    listing_type: z.string().min(1, errorMessages.required),
    internalname: z
      .string()
      .min(1, errorMessages.required)
      .min(3, errorMessages.minLength(3))
      .transform((internalname) => internalname.toUpperCase()),
    subtype: z.enum(['private_room', 'entire_home', 'shared_room'], {
      errorMap: () => ({ message: 'Selecione um subtipo válido' }),
    }),
  })

  if (staysProperty) {
    basicInfoSchema = basicInfoSchema.merge(basicInfoSchemaStaysExtended)
  }

  // Step 2: Property Details (Required)
  const propertyDetailsSchema = z.object({
    bedrooms: z
      .number()
      .min(1, errorMessages.minValue(1))
      .max(20, errorMessages.maxValue(20)),

    bathrooms: z
      .number()
      .min(1, errorMessages.minValue(1))
      .max(20, errorMessages.maxValue(20)),

    max_guests: z
      .number()
      .min(1, errorMessages.minValue(1))
      .max(50, errorMessages.maxValue(50)),

    beds: z
      .number()
      .min(0, errorMessages.minValue(0))
      .max(50, errorMessages.maxValue(50))
      .optional(),

    amenities: z.array(z.string()),

    status: z.enum(['active', 'inactive', 'draft', 'hidden']).default('draft'),
  })

  // Step 3: Access Information & Instructions (Optional - Merged)
  const accessInstructionsSchema = z.object({
    wifi_name: z.string().max(50, errorMessages.maxLength(50)).optional(),

    wifi_password: z.string().max(50, errorMessages.maxLength(50)).optional(),

    access_code: z.string().max(20, errorMessages.maxLength(20)).optional(),

    checkin_time: z
      .string()
      .transform(
        (value) => value.replace(/(\d{2}):(\d{2})/, '$1:$2') || '11:00'
      )
      .refine(
        (value) => {
          const [hours, minutes] = value.split(':').map(Number)
          return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60
        },
        {
          message: 'Hora deve ser válida (HH:MM)',
        }
      )
      .default('15:00'),

    checkout_time: z
      .string()
      .transform(
        (value) => value.replace(/(\d{2}):(\d{2})/, '$1:$2') || '11:00'
      )
      .refine(
        (value) => {
          const [hours, minutes] = value.split(':').map(Number)
          return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60
        },
        {
          message: 'Hora deve ser válida (HH:MM)',
        }
      )
      .default('11:00'),

    checkin_instructions: z
      .string()
      .max(2000, errorMessages.maxLength(2000))
      .optional(),

    checkout_instructions: z
      .string()
      .max(2000, errorMessages.maxLength(2000))
      .optional(),
  })

  // Step 4: Costs and Management (Required)
  const costsManagementSchema = z.object({
    cleaning_fee: z
      .string()
      .refine(
        (value) => {
          const num = parseFloat(value)
          return !isNaN(num) && num >= 0
        },
        {
          message: 'Taxa de limpeza deve ser um número positivo.',
        }
      )
      .transform(parseFloat),

    management_cost_percentage: z
      .string()
      .refine(
        (value) => {
          const num = parseFloat(value)
          return !isNaN(num) && num >= 0 && num <= 100
        },
        {
          message: 'A porcentagem deve ser um número entre 0 e 100.',
        }
      )
      .transform(parseFloat),
  })

  // Step 5: Images (Optional)
  const imagesSchema = z.object({
    images: z
      .array(
        z.object({
          url: z.string(),
          is_primary: z.boolean().nullable(),
          stays_id: z.string().nullable(),
          file: z.instanceof(File).optional(), // For new uploads
        })
      )
      .max(10, 'Máximo de 10 imagens permitidas'),
  })

  // Complete property schema
  const propertySchema = basicInfoSchema
    .merge(propertyDetailsSchema)
    .merge(accessInstructionsSchema)
    .merge(costsManagementSchema)
    .merge(imagesSchema)
  // Step validation schemas for guided form
  const stepSchemas = {
    basicInfo: basicInfoSchema,
    propertyDetails: propertyDetailsSchema,
    accessInstructions: accessInstructionsSchema,
    costsManagement: costsManagementSchema,
    images: imagesSchema,
  } as const

  // Required steps for form completion
  const requiredSteps = [
    'basicInfo',
    'propertyDetails',
    'costsManagement',
  ] as const
  const optionalSteps = ['accessInstructions', 'images'] as const

  return {
    propertySchema,
    stepSchemas,
    requiredSteps,
    optionalSteps,
  }
}

export type StepSchemas = ReturnType<typeof createPropertySchema>['stepSchemas']

// Types with overloads for conditional typing
export type PropertyFormInput<T extends boolean = false> = T extends true
  ? z.input<ReturnType<typeof createPropertySchema>['propertySchema']> & {
      _idproperty: string
      listing_type: string
      internalname: string
      subtype: string
    }
  : z.input<ReturnType<typeof createPropertySchema>['propertySchema']>

export type PropertyFormOutput<T extends boolean = false> = T extends true
  ? z.output<ReturnType<typeof createPropertySchema>['propertySchema']> & {
      _idproperty: string
      listing_type: string
      internalname: string
      subtype: string
    }
  : z.output<ReturnType<typeof createPropertySchema>['propertySchema']>
